import { addDays, isSameDay } from "date-fns";
import { ref } from "vue";
import { toJSDate } from '@/lib/timeUtils';

/**
 * Composable for copying and pasting shifts
 * @param {Ref<Array>} events - Reference to calendar events
 * @param {Object} shiftsService - Service for managing shifts
 * @returns {Object} - Methods for copying and pasting shifts
 */
export function useShiftCopy(events, shiftsService) {
  const copiedShifts = ref([]);
  const copyType = ref(null);
  const copyMeta = ref(null);

  /**
   * Copy a single shift
   * @param {Object} shift - The shift to copy
   * @returns {Object} - Result object
   */
  const copyShift = (shift) => {
    copiedShifts.value = [shift];
    copyType.value = "single";
    return { success: true, message: "Shift copied" };
  };

  /**
   * Copy all shifts for a specific user in a week
   * @param {Number} userId - User ID
   * @param {Date} weekStart - Start date of the week
   * @returns {Object} - Result object
   */
  const copyUserWeek = (userId, weekStart) => {
    // Ensure events.value is an array
    if (!Array.isArray(events.value)) {
      return {
        success: false,
        message: "No shifts data available",
      };
    }

    const shiftsForUser = events.value.filter(
      (e) =>
        e.userId === userId &&
        toJSDate(e.start) >= weekStart &&
        toJSDate(e.start) <= addDays(weekStart, 6)
    );

    if (shiftsForUser.length === 0) {
      return {
        success: false,
        message: "No shifts found for this user in the selected week",
      };
    }

    copiedShifts.value = shiftsForUser;
    copyType.value = "week_user";
    copyMeta.value = { weekStart };

    return {
      success: true,
      message: `Copied ${shiftsForUser.length} shifts for this user`,
    };
  };

  /**
   * Copy all shifts in a week
   * @param {Date} weekStart - Start date of the week
   * @returns {Object} - Result object
   */
  const copyAllWeek = (weekStart) => {
    // Ensure events.value is an array
    if (!Array.isArray(events.value)) {
      return {
        success: false,
        message: "No shifts data available",
      };
    }

    // Use week start and end to capture all shifts in the week, including Saturday

    const shiftsInWeek = events.value.filter(
      (e) => {
        const shiftStart = toJSDate(e.start);
        // IMPORTANT CHANGE: Adjust our week inclusion logic to ensure we catch Saturdays consistently
        // Some date comparisons might be failing with time-based calculations for boundary days
        const weekEnd = addDays(weekStart, 6); // Saturday
        
        // Method 1: Using pure date comparison
        const dateOnlyCheck = shiftStart >= weekStart && shiftStart <= weekEnd;
        
        // Method 2: Using day of week and compare to weekStart's date
        const shiftDay = shiftStart.getDay(); // 0=Sun, 6=Sat
        const shiftDate = shiftStart.getDate();
        const weekStartDay = weekStart.getDay(); // Should be 0 (Sunday)
        const weekStartDate = weekStart.getDate();
        const weekEndDate = weekEnd.getDate();
        
        // Is this shift within the correct date range for its day of week?
        const dayBasedCheck = 
          (shiftDay >= weekStartDay) && // Day is later in week than start day
          (shiftDate >= weekStartDate && shiftDate <= weekEndDate); // Date is within range
        
        // Combine both methods with logging - TRY FORCING SATURDAY INCLUSION
        let isInWeek = dateOnlyCheck;
        
        // IMPORTANT: If it's Saturday, force inclusion if the date matches the week
        // This is the core fix for the Saturday shift issue
        if (shiftDay === 6) {
          // For Saturday (day 6), use a more reliable method that handles month boundaries
          // Create the actual Saturday of the week and compare by date
          const saturday = addDays(weekStart, 6); // This is the actual Saturday
          const saturdayDate = saturday.getDate();
          const saturdayMonth = saturday.getMonth();
          
          // Check if this shift is on the correct Saturday (same date and month)
          const isCorrectSaturday = 
            shiftDate === saturdayDate && 
            shiftStart.getMonth() === saturdayMonth;
          
          // If this is the right Saturday based on the date, force include it
          if (isCorrectSaturday) {
            isInWeek = true;
          }
        }
        
        // No debug logging needed
        
        // Check if the shift is in the target week
        return isInWeek;
      }
    );

    if (shiftsInWeek.length === 0) {
      return {
        success: false,
        message: "No shifts found in the selected week",
      };
    }

    copiedShifts.value = shiftsInWeek;
    copyType.value = "week_all";
    copyMeta.value = { weekStart };

    return {
      success: true,
      message: `Copied ${shiftsInWeek.length} shifts for this week`,
    };
  };

  /**
   * Copy all shifts for a specific day
   * @param {Date} date - The day to copy
   * @returns {Object} - Result object
   */
  const copyAllDay = (date) => {
    // Ensure events.value is an array
    if (!Array.isArray(events.value)) {
      return {
        success: false,
        message: "No shifts data available",
      };
    }

    const targetDate = new Date(date);
    const shiftsOnDay = events.value.filter((e) =>
      isSameDay(toJSDate(e.start), targetDate)
    );

    if (shiftsOnDay.length === 0) {
      return {
        success: false,
        message: "No shifts found for this day",
      };
    }

    copiedShifts.value = shiftsOnDay;
    copyType.value = "day_all";
    copyMeta.value = { date: targetDate };

    return {
      success: true,
      message: `Copied ${shiftsOnDay.length} shifts for this day`,
    };
  };

  /**
   * Check if a shift would be a duplicate on the target date
   * @param {Object} shift - The shift to check
   * @param {Date} targetDate - Target date
   * @returns {Object|null} - Duplicate shift if found, null otherwise
   */
  const isDuplicateShift = (shift, targetDate) => {
    // Guard against invalid inputs
    if (!shift || !targetDate) {
      return null;
    }

    try {
      // Create temporary shift object with target date times for comparison
      const targetStart = new Date(targetDate);
      const shiftStartJS = toJSDate(shift.start);
      targetStart.setHours(shiftStartJS.getHours());
      targetStart.setMinutes(shiftStartJS.getMinutes());

      const shiftEndJS = toJSDate(shift.end);
      const duration = shiftEndJS - shiftStartJS;
      const targetEnd = new Date(targetStart.getTime() + duration);

      // Make sure events.value is an array
      if (!Array.isArray(events.value)) {
        return null;
      }

      // Get existing shifts for this user on target date
      const userId = shift.extendedProps?.userId || shift.userId;
      if (!userId) {
        return null;
      }

      const existingUserShifts = events.value.filter(
        (e) =>
          (e.extendedProps?.userId === userId || e.userId === userId) &&
          isSameDay(toJSDate(e.start), targetDate)
      );

      // Check for time overlap
      if (
        !Array.isArray(existingUserShifts) ||
        existingUserShifts.length === 0
      ) {
        return null;
      }

      return existingUserShifts.find((e) => {
        // Check if time ranges overlap
        const existingStart = toJSDate(e.start);
        const existingEnd = toJSDate(e.end);

        // Consider shifts duplicates if they have:
        // 1. Same user
        // 2. Times within 15 minutes of each other
        const startTimeDiff = Math.abs(existingStart - targetStart);
        const endTimeDiff = Math.abs(existingEnd - targetEnd);
        const fifteenMinutes = 15 * 60 * 1000;

        return startTimeDiff < fifteenMinutes && endTimeDiff < fifteenMinutes;
      });
    } catch (error) {
      // If any error occurs during check, return null (no duplicate)
      console.error("Error checking for duplicate shift:", error);
      return null;
    }
  };

  /**
   * Paste a single shift to a new date
   * @param {Date} targetDate - Target date for paste
   * @returns {Object} - Result object
   */
  const pasteSingleShift = async (targetDate) => {
    if (copiedShifts.value.length === 0 || copyType.value !== "single") {
      return { success: false, message: "No shift has been copied" };
    }

    try {
      const s = copiedShifts.value[0];
      const sStartJS = toJSDate(s.start);
      const sEndJS = toJSDate(s.end);
      const duration = sEndJS - sStartJS;
      const newStart = new Date(targetDate);
      newStart.setHours(sStartJS.getHours());
      newStart.setMinutes(sStartJS.getMinutes());
      const newEnd = new Date(newStart.getTime() + duration);

      // Check for duplicates
      const duplicate = isDuplicateShift(s, targetDate);
      if (duplicate) {
        return {
          success: false,
          message: "This would create a duplicate shift",
          duplicate: true,
        };
      }

      await saveNewShift(s, newStart, newEnd);
      return { success: true, message: "Shift pasted successfully" };
    } catch (err) {
      return {
        success: false,
        message: `Failed to paste shift: ${err.message}`,
      };
    }
  };

  /**
   * Paste a week of shifts to a new week
   * @param {Date} weekTargetStart - Target week start date
   * @returns {Object} - Result object
   */
  const pasteWeek = async (weekTargetStart) => {
    if (copiedShifts.value.length === 0 || !copyType.value.startsWith("week")) {
      return { success: false, message: "No week has been copied" };
    }

    try {
      const fromWeekStart = copyMeta.value.weekStart;
      let pastedCount = 0;
      let skippedCount = 0;

      // Process all shifts from source week to target week

      for (const s of copiedShifts.value) {
        const original = toJSDate(s.start);
        const originalDay = original.getDay(); // 0=Sunday, 6=Saturday
        const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][originalDay];
        
        // Calculate normalized dates for comparison (midnight times)
        const dayDiff = new Date(original);
        dayDiff.setHours(0, 0, 0, 0);
        const weekStartCopy = new Date(fromWeekStart);
        weekStartCopy.setHours(0, 0, 0, 0);
        
        // METHOD 1: Calculate using date math (original approach) 
        const diffTime = dayDiff - weekStartCopy;
        const mathDayOffset = Math.round(diffTime / (1000 * 60 * 60 * 24));
        
        // METHOD 2: Calculate using day of week directly
        const dayOfWeek = originalDay;
        
        // METHOD 3: Calculate using simple difference in days since Sunday
        // This creates a more direct mapping to target week days
        const daysFromTargetSunday = dayOfWeek;
        
        // Use the day of week index directly as the offset
        // This is the key fix that ensures Saturday shifts work correctly
        const dayOffset = dayOfWeek;
        
        // Calculate the final target date using our chosen method
        const newStart = addDays(weekTargetStart, dayOffset);
        newStart.setHours(original.getHours(), original.getMinutes());
        const sEndJS = toJSDate(s.end);
        const newEnd = new Date(
          newStart.getTime() + (sEndJS - original)
        );
        
        // No debug logging needed

        // Check for duplicates
        const duplicate = isDuplicateShift(s, newStart);
        if (duplicate) {
          skippedCount++;
          continue; // Skip this shift
        }
        
        try {
          await saveNewShift(s, newStart, newEnd);
          pastedCount++;
        } catch (error) {
          throw error; // Re-throw to outer catch block
        }
      }

      // Prepare success message
      let message = `Pasted ${pastedCount} shifts successfully`;
      if (skippedCount > 0) {
        message += ` (${skippedCount} duplicate shifts were skipped)`;
      }

      return {
        success: true,
        message: message,
      };
    } catch (err) {
      return {
        success: false,
        message: `Failed to paste week: ${err.message}`,
      };
    }
  };

  /**
   * Paste a day of shifts to a new day
   * @param {Date} targetDate - Target date
   * @returns {Object} - Result object
   */
  const pasteDay = async (targetDate) => {
    if (copiedShifts.value.length === 0 || copyType.value !== "day_all") {
      return { success: false, message: "No day has been copied" };
    }

    try {
      const sourceDate = copyMeta.value.date;
      let pastedCount = 0;
      let skippedCount = 0;

      for (const s of copiedShifts.value) {
        const original = toJSDate(s.start);

        // Create new start time with same time but on target date
        const newStart = new Date(targetDate);
        newStart.setHours(original.getHours(), original.getMinutes(), 0, 0);

        // Calculate duration and set end time accordingly
        const sEndJS = toJSDate(s.end);
        const duration = sEndJS - original;
        const newEnd = new Date(newStart.getTime() + duration);

        // Check for duplicates
        const duplicate = isDuplicateShift(s, targetDate);
        if (duplicate) {
          skippedCount++;
          continue; // Skip this shift
        }

        await saveNewShift(s, newStart, newEnd);
        pastedCount++;
      }

      let message = `Pasted ${pastedCount} shifts successfully`;
      if (skippedCount > 0) {
        message += ` (${skippedCount} duplicate shifts were skipped)`;
      }

      return {
        success: true,
        message: message,
      };
    } catch (err) {
      return {
        success: false,
        message: `Failed to paste day: ${err.message}`,
      };
    }
  };

  /**
   * Save a new shift to the database
   * @param {Object} s - Source shift
   * @param {Date} start - New start time
   * @param {Date} end - New end time
   * @returns {Promise<Object>} - Saved shift
   */
  const saveNewShift = async (s, start, end) => {
    // Validate dates
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error("Invalid date");
    }

    const userId =
      s.userId || (s.extendedProps ? s.extendedProps.userId : null);
    const storeId =
      s.storeId || (s.extendedProps ? s.extendedProps.storeId : null);
    const notes = s.notes || (s.extendedProps ? s.extendedProps.notes : "");

    if (!userId || !storeId) {
      throw new Error("Missing required data (user or store)");
    }

    const shiftData = {
      userId,
      storeId,
      startTime: start,
      endTime: end,
      notes,
    };

    // Use skipConflictCheck option since we're already handling duplicates
    return await shiftsService.createShift(shiftData, {
      skipDuplicateCheck: true,
      // We still want conflict detection, just not duplicate detection
      skipConflictCheck: false,
    });
  };

  return {
    copiedShifts,
    copyType,
    copyMeta,
    copyShift,
    copyUserWeek,
    copyAllWeek,
    copyAllDay,
    pasteSingleShift,
    pasteWeek,
    pasteDay,
    isDuplicateShift,
  };
}